/**
 * Bridges native-shell deep-link delivery into the React Router app.
 *
 * On Electron desktop the auth flow sends the user to the system
 * browser, which returns to the app via a `quadratic://` URL. The
 * native shell surfaces that URL to JS; we translate it into an in-app
 * navigation so the existing React Router routes (notably
 * `/login-result`) can run their loaders and finish the OAuth code
 * exchange.
 *
 * Called once from `index.tsx` during bootstrap — before React mounts —
 * so the cold-start deep link (the URL the OS used to launch the app)
 * is replayed into the router before any route loaders fire.
 */
import { isElectron } from '@/app/nativeBridge';
import { ROUTES } from '@/shared/constants/routes';

// sessionStorage key for the most recently processed deep-link URL.
// sessionStorage survives WebView reloads within the same app session,
// which is exactly the scope we need. The Electron path consumes the
// initial URL in main, but we still de-dupe here in case
// `getInitialDeepLink()` is called more than once.
const CONSUMED_DEEP_LINK_KEY = '__quadratic_consumed_deep_link__';

// Map a `quadratic://<host><path>?<qs>` URL to the equivalent in-app
// path. We currently only recognize the login-result callback; everything
// else is logged and ignored so unrecognized schemes don't hijack the app.
const toInAppPath = (rawUrl: string): string | null => {
  let parsed: URL;
  try {
    parsed = new URL(rawUrl);
  } catch {
    return null;
  }
  // Look at both the hostname and the path. The canonical WorkOS redirect
  // URI is `quadratic://auth-callback/login-result` (needed so authkit-js's
  // pathname-based callback detection works — see `workos.ts`), but custom
  // schemes also parse inconsistently across platforms, so we also accept
  // legacy forms like `quadratic://login-result?...` where the "key"
  // landed in the host instead of the path.
  const host = parsed.hostname;
  const path = parsed.pathname.replace(/^\/+/, '').replace(/\/+$/, '');

  if (host === 'billing-callback' && (path === 'open' || path.endsWith('/open'))) {
    const target = parsed.searchParams.get('target');
    if (!target) return null;
    let decoded: string;
    try {
      decoded = decodeURIComponent(target);
    } catch {
      return null;
    }
    // The target can only ever be an in-app relative path: a single leading
    // '/', no backslashes (treated as path separators on Windows), and no
    // URL-scheme prefix (open-redirect protection).
    if (!decoded.startsWith('/') || decoded.startsWith('//')) return null;
    if (decoded.includes('\\')) return null;
    // Anchor the scheme check to the start of the path so legitimate paths that
    // merely contain '://' mid-string (e.g. `/file/a://b`) aren't rejected.
    const pathOnly = decoded.split('?')[0]?.split('#')[0] ?? '';
    if (/^[a-z][a-z0-9+.-]*:/i.test(pathOnly)) return null;
    // Reject still-encoded path separators (e.g. `%2F%2F`, `%5C`) so a value
    // that only becomes `//` or `\` after a second decode can't slip through.
    if (/%2f|%5c/i.test(decoded)) return null;
    return decoded;
  }

  if (host === 'login-result' || path === 'login-result' || path.endsWith('/login-result')) {
    return `${ROUTES.LOGIN_RESULT}${parsed.search}${parsed.hash}`;
  }
  return null;
};

const navigateWebView = (target: string): void => {
  // Use replace() instead of assign() so the intermediate deep-link URL
  // doesn't end up in the WebView's history and the back button returns
  // to whatever the user was doing before signing in.
  window.location.replace(target);
};

// Returns true if the URL triggered a WebView navigation (so the caller
// knows a reload is imminent and should not wire up further listeners).
const handleDeepLinkUrl = (rawUrl: string): boolean => {
  const target = toInAppPath(rawUrl);
  if (!target) {
    console.warn('[nativeDeepLinks] ignoring unrecognized deep-link URL:', rawUrl);
    return false;
  }

  try {
    if (window.sessionStorage.getItem(CONSUMED_DEEP_LINK_KEY) === rawUrl) {
      return false;
    }
    window.sessionStorage.setItem(CONSUMED_DEEP_LINK_KEY, rawUrl);
  } catch {
    // sessionStorage unavailable (private mode, etc.) — fall through and
    // accept the risk of a double navigation rather than blocking auth.
  }

  // Extra safety: if we happen to already be at the target path (e.g. a
  // previous replace() raced with a React Router redirect), don't reload.
  const current = window.location.pathname + window.location.search + window.location.hash;
  if (current === target) {
    return false;
  }

  navigateWebView(target);
  return true;
};

const initElectron = async (): Promise<void> => {
  const electron = window.electron;
  if (!electron) return;

  // Cold start: the OS may have launched the app with a deep link.
  // Main consumes this and returns null on subsequent calls.
  const initial = await electron.getInitialDeepLink();
  if (initial) {
    const navigated = handleDeepLinkUrl(initial);
    if (navigated) return;
  }

  // Warm start: app is already running and the OS hands it a new URL
  // (e.g. user clicked a `quadratic://` link while the app was open).
  electron.onDeepLink((url) => {
    handleDeepLinkUrl(url);
  });
};

export const initNativeDeepLinks = async (): Promise<void> => {
  try {
    if (isElectron()) {
      await initElectron();
      return;
    }
  } catch (error) {
    console.warn('[nativeDeepLinks] failed to initialize:', error);
  }
};
